<template>
  <v-inventory-create
    v-if="access.create_reciept"
    name="receipt"
    ref="receipt"
    top_title="create_receipt"
    no-additional-cost
    no-note
  >
    <v-layout row class="px-2">
      <v-flex class="px-1">
        <v-autocomplete
          v-model="params.service"
          :label="$t('store')"
          v-bind:items="stores"
          item-text="name"
          item-value="_id"
          ref="service"
          :rules="[fieldRules.required]"
        />
      </v-flex>
      <v-flex class="px-1">
        <v-autocomplete
          v-model="params.user_id"
          :label="$t('client')"
          v-bind:items="customers"
          item-value="_id"
          item-text="first_name"
          v-on:update:search-input="searchCustomers"
          @change="ClientDisabled"
        />
      </v-flex>
    </v-layout>
    <v-layout row class="px-2">
      <v-text-field
        v-model="params.comment"
        :label="$t('comment')"
        counter="500"
      />
      <v-list-item class="ma-0 pa-0">
        <v-list-item-content>
          <v-list-item-title class="font-weight-medium">
            {{ $t("is_refund") }}
          </v-list-item-title>
        </v-list-item-content>
        <v-list-item-action>
          <v-switch v-model="params.is_refund"></v-switch>
        </v-list-item-action>
      </v-list-item>
    </v-layout>

    <template #items>
      <v-dialog v-model="receipt_letter_dialog" :width="600" persistent>
        <v-card>
          <v-card-title>
            {{ $t("enter_receipt_letter") }}
          </v-card-title>
          <v-card-text>
            <v-text-field
              v-model="receipt_letter"
              :label="$t('receipt_letter')"
              autocomplete="off"
              v-mask="['A', 'AA']"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              color="primary"
              :disabled="!receipt_letter"
              @click="setReceiptNumber"
            >
              {{ $t("confirm") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-data-table
        v-bind:headers="headers"
        disable-sort
        v-bind:items="items"
        :items-per-page="200"
        hide-default-footer
        :mobile-breakpoint="0"
        :no-data-text="$t('you_have_not_selected_items_yet')"
        :loading="loadingItems"
      >
        <template #[`item.product_name`]="{ item }">
          <v-list-item-content
            style="
              width: 350px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            "
          >
            <v-list-item-title>{{ item.product_name }}</v-list-item-title>
            <v-list-item-subtitle>SKU {{ item.sku }}</v-list-item-subtitle>
          </v-list-item-content>
        </template>
        <template #[`item.p_order`]="{ item }">
          <v-text-field
            v-model="item.p_order"
            :append-icon="'mdi-chevron-down'"
            @click="PartitionGetItem(item)"
            :suffix="item.p_order"
            readonly
          />
        </template>
        <template #[`item.value`]="{ item }">
          <v-text-field
            v-model.number="item.value"
            @input="changeItemValue(item)"
            type="number"
            :ref="'value' + item._id"
            @focus="$event.target.select()"
            class="text-right"
            autocomplete="off"
            :rules="[ValueMax(item.value, item.value_max)]"
          >
          </v-text-field>
        </template>
        <template #[`item.qty_box`]="{ item }">
          <v-text-field
            v-model.number="item.qty_box"
            type="number"
            :ref="'qty_box' + item._id"
            @focus="$event.target.select()"
            class="text-right"
            autocomplete="off"
          />
        </template>
        <template #[`item.reminder`]="{ item }">
          <v-text-field
            v-if="item.sold_by == 'box' || item.sold_by == 'pcs'"
            v-model.number="item.reminder"
            @input="changeItemReminder(item)"
            type="number"
            :ref="'reminder' + item._id"
            @focus="$event.target.select()"
            class="text-right"
            autocomplete="off"
          />
        </template>

        <template #[`item.default_purchase_cost`]="{ item }">
          {{
            item.default_purchase_cost
              ? item.default_purchase_cost.toLocaleString()
              : 0
          }}
        </template>

        <template #[`item.price`]="{ item }">
          <vuetify-money
            v-model.number="item.price"
            :ref="'price' + item.product_id"
            :rules="[fieldRules.require]"
            v-bind:options="
              item.currency == 'usd' ? moneyOptionsUSD : moneyOptions
            "
            @input="changeItemPrice(item)"
          />
        </template>

        <template #[`item.price_for_pieces`]="{ item }">
          <vuetify-money
            v-if="item.sold_by == 'box' || item.sold_by == 'pcs'"
            v-model.number="item.price_for_pieces"
            v-bind:options="
              item.currency == 'usd' ? moneyOptionsUSD : moneyOptions
            "
            @input="changeItemPricePiece(item)"
          />
        </template>

        <template #[`item.currency`]="{ item }">
          <v-select v-model="item.currency" :items="currencies" />
        </template>

        <template #[`item.total`]="{ item }">
          <vuetify-money
            v-model.number="item.total"
            :ref="'total' + item.product_id"
            :rules="[fieldRules.require]"
            v-bind:options="
              item.currency == 'usd' ? moneyOptionsUSD : moneyOptions
            "
            @input="changeItemTotal(item)"
          ></vuetify-money>
        </template>

        <template #[`item.remove`]="{ item }">
          <v-btn icon v-on:click="remove_item(item)">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>

      <v-divider />
      <v-card-text>
        <v-search-items
          v-bind:label="$t('add_item')"
          v-bind:items="items_list"
          v-bind:selectedAct="add_item"
          v-bind:rules="items && items.length == 0 ? [fieldRules.required] : []"
          v-bind:searchItems="getReceiptItems"
        />
      </v-card-text>

      <v-card-text>
        <v-layout row px-2>
          <v-flex md8 px-2> </v-flex>
          <v-flex md4 px-2>
            <p
              v-if="
                (currency.currency == 'usd' &&
                  Math.abs(total_difference) > 0.01) ||
                (Math.abs(total_difference) > 1 && currency.currency != 'usd')
              "
              class="body-2 font-weight-bold red--text"
            >
              {{ total_difference.toLocaleString() }}
              {{ $t(currency.currency) }}
            </p>
            <v-data-table
              v-if="!params.is_refund"
              v-bind:headers="payment_headers"
              disable-sort
              v-bind:items="payment_items"
              :items-per-page="10"
              hide-default-footer
              :mobile-breakpoint="0"
              hide-default-header
            >
              <template #[`item.name`]="{ item }">
                {{ $t(item.name) }}
              </template>
              <template #[`item.value`]="{ item }">
                <vuetify-money
                  v-bind:disabled="
                    !clientDebitDesable_id && item.name == 'debt' ? true : false
                  "
                  v-model.number="item.value"
                  :rules="[fieldRules.require]"
                  v-bind:options="
                    currency.currency == 'usd' ? moneyOptionsUSD : moneyOptions
                  "
                  @input="setPaymentsDefault"
                  @click="setPaymentsDefault"
                ></vuetify-money>
              </template>
              <template #footer>
                <v-layout row px-5 mt-5>
                  <v-spacer />
                  <p class="body-1 font-weight-bold">
                    {{ total_price.toLocaleString() }}
                    {{ $t(currency.currency) }}
                  </p>
                </v-layout>
              </template>
            </v-data-table>
            <v-layout v-else row px-5 mt-5>
              <v-spacer />
              <p class="body-1 font-weight-bold black--text">
                {{ total_price.toLocaleString() }}
                {{ $t(currency.currency) }}
              </p>
            </v-layout>
          </v-flex>
        </v-layout>
      </v-card-text>
    </template>

    <template #action>
      <v-btn v-on:click="cancel" text>{{ $t("cancel") }}</v-btn>
      <v-btn
        color="success"
        text
        v-on:click="create"
        :loading="saving_document"
        :disabled="saveDisabled || savetotalDisabled || isoffline"
        >Create
      </v-btn>
    </template>
    <v-dialog v-model="partition_dialog" persistent width="700">
      <v-card>
        <v-card-title>
          <span class="text-h5">Partition</span>
        </v-card-title>
        <v-data-table
          :headers="partition_headers"
          :items="partition_items"
          hide-default-footer
          @click:row="RowClick"
        >
          <template #[`item.quantity`]="{ item }">
            {{ item.quantity }} /{{ item.quantity_left }}
          </template>
        </v-data-table>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelPatitionClicked" color="primary"
            >cancel</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-inventory-create>
  <error404 v-else></error404>
</template>
<script>
import Services from "../../mixins/services";
import Currency from "../../mixins/settings/currency";
import Customers from "../../mixins/customer/customer";
import Userable from "../../mixins/userable";
import Payment from "../../mixins/payment";

export default {
  name: "PurchaseCreate",
  mixins: [Userable, Services, Currency, Customers, Payment],
  data: () => ({
    items: [],
    items_list: [],
    item: null,
    params: {
      service: null,
      comment: "",
      total_currency: "uzs",
      is_refund: false,
    },
    search_text: "123",
    noAllStores: true,
    exist_dialog: false,
    exist_item: null,
    receipt_id: "",
    receipts: [],
    upload_errors: [],
    loadingItems: false,
    saving_document: false,
    payment_items: [
      {
        name: "cash",
        value: 0,
        index: 1,
      },
    ],
    last_client_search: "123",
    receipt_letter_dialog: false,
    receipt_letter: null,
    access: null,
    partion_id: "",
    savetotalDisabled: false,
    partition_dialog: false,
    partition_items: [],
    partition_headers: [
      { text: "Purchase Order", value: "p_order", sortable: false },
        { text: "Purchase No", value: "partiation_no", sortable: false },
      { text: "Supplier", value: "supplier_name", sortable: false },
      {
        text: "Store",
        value: "service_name",
        align: "center",
        sortable: false,
      },
      { text: "In stock", value: "quantity", align: "center", sortable: false },
    ],
    clientDebitDesable_id: "",
    isoffline: false,
    ParamsClientId: "",
    ParamsUserId: "",
  }),
  computed: {
    receiptNumber() {
      return this.$store.state.receipt_letter;
    },
    headers() {
      let headers = [
        { text: this.$t("product_name"), value: "product_name" },
        {
          text: this.$t("partition"),
          value: "p_order",
          width: 120,
          align: "right",
        },
        {
          text: this.$t("qty_box"),
          value: "qty_box",
          width: 90,
          align: "right",
        },
        // {
        //   text: this.$t("reminder"),
        //   value: "reminder",
        //   width: 90,
        //   align: "right",
        // },
        {
          text: this.$t("cost"),
          value: "default_purchase_cost",
          width: 120,
          align: "right",
        },
        {
          text: this.$t("price"),
          value: "price",
          width: 120,
          align: "right",
        },
        // {
        //   text: this.$t("price_for_pieces"),
        //   value: "price_for_pieces",
        //   width: 120,
        //   align: "right",
        // },
        {
          text: this.$t("value"),
          value: "value",
          width: 90,
          align: "right",
        },
        {
          text: this.$t("total"),
          value: "total",
          width: 120,
          align: "right",
        },
      ];

      headers.push({ text: null, value: "remove", align: "center", width: 50 });
      return headers;
    },
    total_price() {
      let sum = 0;
      if (!this.currency.value) return sum;
      const currency_value = this.roundMoney(this.currency.value);

      for (const item of this.items) {
        let initial = 0;
        if (this.has_count_by_type_item(item)) {
          initial = (item.reminder * item.price) / item.count_by_type;
        }
        if (item.currency == this.currency.currency) {
          sum += this.roundMoney(initial + item.value * item.price);
        } else {
          if (this.currency.currency == "usd") {
            sum += this.roundMoney(
              (initial + item.value * item.price) / currency_value
            );
          } else {
            sum += this.roundMoney(
              initial + item.value * item.price * currency_value
            );
          }
        }
      }

      return sum;
    },
    total_difference() {
      let payments = 0;
      for (const p of this.payment_items) {
        if (p.value) {
          payments += parseFloat(p.value);
        }
      }

      return this.total_price - payments;
    },
    form() {
      const fObj = {
        service: this.params.service,
      };

      for (let i = 0; i < this.items.length; i++) {
        fObj["price" + this.items[i].product_id] = this.items[i].price;
        fObj["total" + this.items[i].product_id] = this.items[i].total;
      }

      return fObj;
    },
    saveDisabled() {
      const total_difference =
        (this.currency.currency == "usd" &&
          Math.abs(this.total_difference) > 0.01) ||
        (Math.abs(this.total_difference) > 1 &&
          this.currency.currency != "usd");
      let formHasErrors = false || this.items.length == 0 || total_difference;
      Object.keys(this.form).forEach((f) => {
        if (!this.form[f] && this.form[f] != 0) {
          formHasErrors = true;
        }
      });
      return formHasErrors;
    },
    payment_headers() {
      return [
        { text: this.$t("name"), value: "name" },
        { text: this.$t("value"), value: "value", width: 50, align: "right" },
      ];
    },
  },
  watch: {
    "params.service"() {
      this.getReceiptItems(this.search_text, true);
    },
    stores() {
      if (this.stores && this.stores.length > 0) {
        this.params.service = this.stores[0]._id;
      }
    },
    total_price() {
      this.payment_items[0].value = this.total_price;
    },
    payment_types: {
      deep: true,
      handler() {
        if (!(this.payment_types instanceof Array)) {
          this.payment_types = [];
        }
        const payment_items = [
          {
            name: "cash",
            value: 0,
            index: 1,
          },
        ];
        for (const index in this.payment_types) {
          const payment = this.payment_types[index];
          if (index == 0) continue;
          if (payment.status) {
            payment_items.push({
              name: payment.name,
              value: 0,
              index: index,
            });
          }
        }
        this.payment_items = payment_items;
      },
    },
    receiptNumber() {
      if (this.receiptNumber) {
        this.receipt_letter_dialog = false;
      } else {
        this.receipt_letter_dialog = true;
      }
    },
  },
  methods: {
    ValueMax(v, max) {
      if (max) {
        if (v <= max) {
          this.savetotalDisabled = false;
        } else {
          this.savetotalDisabled = true;
        }
        return v <= max || `Max ${max}`;
      } else {
        return true;
      }
    },
    async PartitionGetItem(item) {
      this.partition_dialog = true;
      const link = `items/partiation/${item.product_id}`;
      const result = await this.$communicator.postMethod2(link, {
        service_id: this.params.service,
      });
      this.partition_items = result.data?.data;
    },
    cancelPatitionClicked() {
      this.partition_dialog = false;
    },
    RowClick(item) {
      const index = this.items.findIndex((i) => i.product_id == item.good_id);
      if (index === -1) return;
      this.items[index].partiation_id = item._id;
      for (const par of this.partition_items) {
        if (par._id == this.items[index].partiation_id) {
          this.items[index].value_max = par.quantity_left;
          this.items[index].p_order = par.p_order;
        }
      }
      this.partition_dialog = false;
    },
    setReceiptNumber() {
      this.$store.commit("setReceiptLetter", this.receipt_letter);
    },
    async searchCustomers(search) {
      if (typeof search != "string") {
        search = "";
      }
      if (this.last_client_search == search) {
        return;
      }
      this.last_client_search = search;
      this.getCustomers(search);
    },
    async ClientDisabled(_id) {
      this.clientDebitDesable_id = _id;
      const res = this.$communicator.then(
        this,
        await this.$communicator("customer_get_by_id", {
          arguments: [_id],
        })
      );
      this.ParamsClientId = res?._id;
      this.ParamsUserId = res?.user_id;
    },
    setPaymentsDefault() {
      for (const index in this.payment_items) {
        if (typeof this.payment_items[index].value != "number") {
          this.payment_items[index].value = 0;
        }
      }
    },
    setTotalAmountCurrency(currency) {
      this.params.total_currency = currency;
    },
    add_item(item) {
      let exist = false;
      let item_index;
      for (const index in this.items) {
        if (this.items[index].product_id == item.product_id) {
          exist = true;
          item_index = index;
        }
      }

      if (exist) {
        this.exist_item = item;
        this.exist_dialog = true;
        let value =
          (item.by_box && this.has_count_by_type_item(item)) ||
          this.has_not_count_by_type_item(item)
            ? 1
            : 0;
        let reminder =
          !item.by_box && this.has_count_by_type_item(item) ? 1 : 0;
        this.items[item_index].value = +this.items[item_index].value + value;
        if (!this.items[item_index].reminder) {
          this.items[item_index].reminder = 0;
        }
        if (!this.items[item_index].value) {
          this.items[item_index].value = 0;
        }
        if (!this.items[item_index].count_by_type) {
          this.items[item_index].count_by_type = 1;
        }
        this.items[item_index].reminder =
          +this.items[item_index].reminder + reminder;
        this.items[item_index].total =
          this.items[item_index].price *
          (this.items[item_index].value +
            this.items[item_index].reminder /
              this.items[item_index].count_by_type);
      } else {
        let value =
          (item.by_box && this.has_count_by_type_item(item)) ||
          this.has_not_count_by_type_item(item)
            ? 1
            : 0;

        let reminder =
          !item.by_box && this.has_count_by_type_item(item) ? 1 : 0;

        if (item.count_by_type && this.has_count_by_type_item(item)) {
          item.total = item.price / item.count_by_type;
        }
        let value_max = null;
        this.items.push({
          ...this.$copy(item),
          value,
          reminder,
          value_max,
          p_order: "",
          qty_box: 0,
        });
      }
    },
    addItem() {
      this.items.push({
        ...this.$copy(this.exist_item),
        number: this.items.length + 1,
      });
      this.exist_item = null;
      this.exist_dialog = false;
    },
    remove_item(item) {
      const index = this.items.indexOf(item);
      this.items.splice(index, 1);
      for (const index in this.items) {
        this.items[index].number = +index + 1;
      }
    },
    async update() {
      this.getReceiptItems();
    },
    async getReceiptItems(search, service_changed = false) {
      if (typeof search != typeof "invan") {
        search = "";
      }

      if (search == this.search_text && !service_changed && search != "123") {
        return;
      }

      this.search_text = search + "";
      const items = this.$communicator.then(
        this,
        await this.$communicator("list_of_inv_items", {
          arguments: [50, 1],
          params: {
            service: this.params.service,
            search: search,
          },
        })
      );
      if (!(items instanceof Array)) {
        return;
      }
      const items_list = [];

      for (const index in items) {
        let search_text = this.getItemSearchText(items[index]);
        let by_box =
          search == items[index].barcode_by_type &&
          typeof items[index].count_by_type === "number";

        let price_for_pieces;

        if (
          this.has_count_by_type_item(items[index]) &&
          items[index].count_by_type
        ) {
          price_for_pieces = items[index].price / items[index].count_by_type;
        }

        items_list.push({
          product_id: items[index]._id,
          product_name: items[index].name,
          search_text: search_text,
          price: this.roundMoney(items[index].price),
          cost: this.roundMoney(items[index].default_purchase_cost),
          total: items[index].price,
          currency: items[index].price_currency,
          sku: items[index].sku,
          barcode_by_type: items[index].barcode_by_type,
          count_by_type: items[index].count_by_type,
          by_box: by_box,
          sold_by: items[index].sold_by,
          price_for_pieces: price_for_pieces,
        });
      }

      this.items_list = items_list;
    },

    async create() {
      this.saving_document = true;
      const params = this.$copy(this.params);
      if (this.customers.length > 0) {
        params.client_id = this.ParamsClientId;
        params.user_id = this.ParamsUserId;
      }
      params.cashier_id = this.user._id;
      params.cashier_name = this.user.name;
      // params.is_refund = false;
      params.is_self = true;
      // this.receiptNumber change this.user.name.slice(0,2).toUpperCase()
      params.receipt_no = this.receiptNumber;
      params.service_value = 0;

      const current_time = new Date().getTime();
      params.date = current_time;
      if (this.params.is_refund) {
        let refund_value = 0;
        for (const val of this.payment_items) {
          if (val.name == "cash") {
            refund_value = val.value;
            val.value = 0;
          }
          if (val.name == "debt") {
            val.value = refund_value;
          }
        }
      }
      params.payment = this.payment_items;
      params.total_price = this.total_price;
      params.currency = this.currency.currency;
      for (const index in this.items) {
        this.items[index].created_time = current_time;
        this.items[index].reset_count = 0;
        this.items[index].discount = [];
        this.items[index].modifiers = [];
        this.items[index].sold_item_type = this.items[index].count_by_type
          ? "pcs_item"
          : "item";
      }
      params.sold_item_list = this.items;
      const response = this.$communicator.then(
        this,
        await this.$communicator("create_receipt", {
          params: params,
        })
      );
      if (response instanceof Array && response.length > 0) {
        this.$toast.success("Success", { timeout: 2000 });
        this.$router.push({
          path: "/reports/receipts",
          query: {
            id: response[0]._id,
          },
        });
      }

      this.saving_document = false;
    },

    async createAndSave() {
      this.saving_document = true;
      const params = this.$copy(this.params);
      params.cashier_id = this.user._id;
      params.cashier_name = this.user.name;

      // params.is_refund = false;
      params.is_self = true;
      params.receipt_no = this.receiptNumber;
      params.service_value = 0;

      const current_time = new Date().getTime();
      params.date = current_time;

      params.payment = this.payment_items;
      params.total_price = this.total_price;
      params.currency = this.currency.currency;

      for (const index in this.items) {
        this.items[index].created_time = current_time;
        this.items[index].reset_count = 0;
        this.items[index].discount = [];
        this.items[index].modifiers = [];
        this.items[index].sold_item_type = this.items[index].count_by_type
          ? "pcs_item"
          : "item";
      }
      params.sold_item_list = this.items;
      const response = this.$communicator.then(
        this,
        await this.$communicator("create_receipt", {
          params: params,
        })
      );
      if (response instanceof Array && response.length > 0) {
        this.$toast.success("Success", { timeout: 2000 });
        this.$router.push({
          path: "/reports/receipts",
          query: {
            id: response[0]._id,
          },
        });
      }

      this.saving_document = false;
    },
    cancel() {
      this.$router.go(-1);
    },
    changeItemValue(item) {
      for (const index in this.items) {
        if (this.items[index].product_id == item.product_id) {
          let initial = 0;
          if (
            this.items[index].sold_by == "box" ||
            this.items[index].sold_by == "pcs"
          ) {
            initial =
              (this.items[index].reminder * this.items[index].price) /
              this.items[index].count_by_type;
          }
          this.items[index].total =
            initial + this.items[index].price * this.items[index].value;
        }
      }
    },
    changeItemReminder(item) {
      for (const index in this.items) {
        if (this.items[index].product_id == item.product_id) {
          if (!this.items[index].value) {
            this.items[index].value = 0;
          }
          let initial = 0;
          if (
            this.items[index].sold_by == "box" ||
            this.items[index].sold_by == "pcs"
          ) {
            if (this.items[index].reminder >= this.items[index].count_by_type) {
              const reminder =
                this.items[index].reminder % this.items[index].count_by_type;
              const count_value = Math.floor(
                this.items[index].reminder / this.items[index].count_by_type
              );
              this.items[index].reminder = reminder;
              this.items[index].value += count_value;
            }
            initial =
              (this.items[index].reminder * this.items[index].price) /
              this.items[index].count_by_type;
          }
          this.items[index].total =
            initial + this.items[index].price * this.items[index].value;
        }
      }
    },
    changeItemPrice(item) {
      for (const index in this.items) {
        if (this.items[index].product_id == item.product_id) {
          let initial = 0;
          if (
            (this.items[index].sold_by == "box" ||
              this.items[index].sold_by == "pcs") &&
            this.items[index].count_by_type
          ) {
            initial =
              (this.items[index].reminder * this.items[index].price) /
              this.items[index].count_by_type;
            this.items[index].price_for_pieces =
              this.items[index].price / this.items[index].count_by_type;
          }
          this.items[index].total =
            initial + this.items[index].price * this.items[index].value;
        }
      }
    },
    changeItemPricePiece(item) {
      for (const index in this.items) {
        if (this.items[index].product_id == item.product_id) {
          let initial = 0;
          if (
            (this.items[index].sold_by == "box" ||
              this.items[index].sold_by == "pcs") &&
            this.items[index].count_by_type
          ) {
            this.items[index].price =
              this.items[index].price_for_pieces *
              this.items[index].count_by_type;
            initial =
              (this.items[index].reminder * this.items[index].price) /
              this.items[index].count_by_type;
          }
          this.items[index].total =
            initial + this.items[index].price * this.items[index].value;
        }
      }
    },
    changeItemTotal(item) {
      for (const index in this.items) {
        if (
          this.items[index].product_id == item.product_id &&
          this.items[index].price
        ) {
          this.items[index].value =
            this.items[index].total / this.items[index].price;
        }
      }
    },
  },
  mounted() {
    if (!this.receiptNumber) {
      this.receipt_letter_dialog = true;
    }
    window.addEventListener(
      "offline",
      () => (
        this.$toast.error("Offline", { timeout: 2000 }),
        (this.saving_document = false),
        (this.isoffline = true)
      )
    );
    window.addEventListener(
      "online",
      () => (
        this.$toast.success("Online", { timeout: 2000 }),
        (this.saving_document = false),
        (this.isoffline = false)
      )
    );
    this.update();
  },
  beforeMount() {
    this.access = this.$store.state.access;
  },
};
</script>
